'use client';

import { SUBSCRIBE_EMAIL_TO_NEWSLETTER } from '@/graphql/mutation/subscribe-email-to-newsletter';
import { validateEmail2 } from '@/util/formValidators';
import { useMutation } from '@apollo/client';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import EventDatePicker from '../event-date-picker';
import FormError from '../FormError';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

const Newsletter = () => {
  const { register, handleSubmit, formState, control } = useForm({
    defaultValues: {
      wedding_role: '',
      email: '',
      event_date: null,
    },
  });
  const [datePickerVisible, setDatePickerVisible] = useState(false);
  const [reqSubscribeEmailToNewsletter, { loading, error, reset: resetMutation, data: subscribeRes }] =
    useMutation(SUBSCRIBE_EMAIL_TO_NEWSLETTER);

  const handleSubmitNewsletter = values => {
    const { event_date } = values;

    reqSubscribeEmailToNewsletter({
      variables: {
        ...values,
        wedding_role: values.wedding_role || undefined,
        event_date: event_date ? dayjs(event_date).format('YYYY-MM-DD') : undefined,
        status: 1,
      },
    });
  };

  return (
    <>
      <form
        className='mt-[23px] lg_mt-5'
        onSubmit={handleSubmit(handleSubmitNewsletter)}
        suppressHydrationWarning
      >
        <div>
          <div className='text-sm leading-5 text-#403732 lg_text-[15px] lg_leading-[21px]'>Wedding Role</div>
          <div className='mt-[13px] grid grid-cols-[auto_auto_auto] justify-start gap-[15px] lg_flex lg_items-center'>
            {[
              {
                label: 'Bride',
                value: 'Bride',
              },
              {
                label: 'Bridesmaid',
                value: 'Bridesmaid',
              },
              {
                label: 'Mom',
                value: 'Mom',
              },
              {
                label: 'Groom',
                value: 'Groom',
              },
              {
                label: 'Groomsmen',
                value: 'Groomsmen',
              },
              {
                label: 'Guest',
                value: 'Guest',
              },
            ].map(({ label, value }) => {
              return (
                <label
                  className='flex cursor-pointer items-center gap-x-2'
                  key={value}
                >
                  <div className='relative inline-block text-[0]'>
                    <input
                      {...register('wedding_role')}
                      type='radio'
                      value={value}
                      className={clsx(
                        'appearance-none',
                        'h-4 w-4',
                        'border-1 border-solid border-E7DED5',
                        'rounded-none outline-0',
                        'cursor-pointer',
                        'flex-none',
                        'bg-transparent checked_bg-80583F',
                        'peer',
                      )}
                      suppressHydrationWarning
                    />
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      width='14'
                      height='12'
                      fill='none'
                      viewBox='0 0 14 12'
                      className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 peer-checked_block'
                    >
                      <path
                        stroke='#fff'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='1.5'
                        d='M13 1 4.6 11 1 7.25'
                      />
                    </svg>
                  </div>

                  <span className='text-[13px] leading-[19px] text-#403732'>{label}</span>
                </label>
              );
            })}
          </div>
        </div>

        <div className='mt-6 lg_mt-5 lg_flex lg_flex-col-reverse'>
          <div className='lg_mt-[15px]'>
            <div className='lg_flex lg_items-center lg_gap-x-[5px] '>
              <div className='relative flex-1 '>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='21'
                  height='16'
                  fill='none'
                  viewBox='0 0 21 16'
                  className='absolute left-[15px] top-1/2 -translate-y-1/2'
                >
                  <path
                    fill='#80583F'
                    d='M20.07.48c-.1-.1-.23-.15-.36-.15L.9.32C.41.32 0 .77 0 1.33v13.48c0 .56.41 1.01.9 1.01h18.7c.5 0 .9-.45.9-1.01V1.33c0-.34-.16-.67-.43-.85m-.64 1.44v12.73H1.07V2.04l8.68 8c.19.17.49.18.69 0zm-9.34 6.92L2.22 1.57l16.01-.08z'
                  />
                </svg>
                <input
                  {...register('email', {
                    validate: validateEmail2,
                  })}
                  onChange={() => {
                    if (error || subscribeRes) {
                      resetMutation();
                    }
                  }}
                  className={clsx(
                    ' h-12 ',
                    'rounded-none  outline-0',
                    'bg-transparent',
                    'flex-1',
                    'pl-[46px] pr-2.5',
                    'text-sm',
                    'placeholder_text-[#6d6b6b]',
                    'border-1 border-solid border-E7DED5',
                    'w-full bg-white',
                  )}
                  placeholder='Enter Your Email Address'
                  suppressHydrationWarning
                />
              </div>

              <button
                type='submit'
                className={clsx(
                  'mt-[15px] lg_mt-0',
                  ' h-12 w-full lg_w-[120px] lg_flex-none ',
                  'flex items-center justify-center',
                  'text-sm leading-none',
                  'rounded-[3px]',
                  'font-medium',
                  'hidden lg_block',
                  'aw-primary-btn',
                )}
                disabled={loading}
              >
                SUBMIT{loading && '...'}
              </button>
            </div>
            <div className='relative'>
              <div className='absolute text-xs text-msgerror'>{formState.errors.email?.message}</div>
            </div>
          </div>

          <Controller
            control={control}
            name='event_date'
            render={({ field: { onChange, value } }) => {
              return (
                <Popover
                  open={datePickerVisible}
                  onOpenChange={nextOpen => {
                    setDatePickerVisible(nextOpen);
                  }}
                >
                  <PopoverTrigger asChild>
                    <button
                      type='button'
                      className='w-full'
                    >
                      <div className='relative mt-[15px] flex h-12 items-center gap-x-2.5 border-1 border-solid border-E7DED5 bg-white pl-[46px] lg_h-[50px]'>
                        <svg
                          xmlns='http://www.w3.org/2000/svg'
                          width='21'
                          height='18'
                          fill='none'
                          viewBox='0 0 21 18'
                          className='absolute left-[15px] top-1/2 -translate-y-1/2'
                        >
                          <path
                            fill='#80583F'
                            d='M18.93 1.944h-3.74v-.71c0-.28-.22-.5-.5-.5s-.5.22-.5.5v.71h-3.63v-.71c0-.28-.22-.5-.5-.5s-.5.22-.5.5v.71H5.93v-.71c0-.28-.22-.5-.5-.5s-.5.22-.5.5v.71H1.19c-.66 0-1.19.54-1.19 1.19v13.08c0 .66.54 1.19 1.19 1.19h17.72c.66 0 1.19-.54 1.19-1.19V3.134c.02-.66-.51-1.19-1.17-1.19m-17.72 1h3.74v.71c0 .28.22.5.5.5s.5-.22.5-.5v-.71h3.63v.71c0 .28.22.5.5.5s.5-.22.5-.5v-.71h3.63v.71c0 .28.22.5.5.5s.5-.22.5-.5v-.71h3.74c.11 0 .19.09.19.19v2.93H1.01v-2.93c0-.1.09-.19.2-.19m17.72 13.47H1.21a.19.19 0 0 1-.19-.19v-9.16h18.11v9.15c-.01.11-.09.2-.2.2'
                          />
                        </svg>
                        <div
                          className={clsx(
                            ' flex h-full items-center ',
                            'rounded-none border-none outline-0',
                            'bg-transparent',
                            'flex-1',
                            'text-sm',
                            !value && 'text-[#6d6b6b]',
                          )}
                        >
                          {value ? dayjs(value).format('MM/DD/YY') : 'What is the date of your event'}
                        </div>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className='shadow-md '>
                    <EventDatePicker
                      onDone={date => {
                        setDatePickerVisible(false);
                        onChange(date);
                      }}
                      value={value}
                    />
                  </PopoverContent>
                </Popover>
              );
            }}
          />
        </div>

        <button
          type='submit'
          className={clsx(
            'mt-[15px] lg_mt-0',
            ' h-12 w-full ',
            'flex items-center justify-center',
            'text-sm leading-none',
            'rounded-[3px]',
            'font-medium',
            'lg_hidden',
            'aw-primary-btn',
          )}
          disabled={loading}
        >
          SUBMIT{loading && '...'}
        </button>
      </form>
      <FormError
        scrollOnError={false}
        allowErrorMessages
        errors={[error]}
      />
      {subscribeRes?.subscribeEmailToNewsletter?.status === 'SUBSCRIBED' ? (
        <div className='relative text-sm'>
          <span className='absolute top-0.5'>Thank you for your subscription! Stay Tuned!</span>
        </div>
      ) : null}
    </>
  );
};

export default Newsletter;
